import { markPublicSaasWebchatContext } from './googleAuth';
import { loadStoredSession } from './sessions';

const PENDING_SAAS_HANDOFF_INTENT_KEY = 'run:pendingSaasHandoffIntent';
const PENDING_SAAS_HANDOFF_BOOTSTRAP_KEY = 'run:pendingSaasHandoffBootstrap';

function readStoredJson<T>(key: string): T | undefined {
  try {
    const rawSession = window.sessionStorage.getItem(key);
    if (rawSession) {
      return JSON.parse(rawSession) as T;
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[run-saas-handoff] pending_session_read_failed', {
      key,
      message: err instanceof Error ? err.message : String(err),
    });
  }

  try {
    const rawLocal = window.localStorage.getItem(key);
    if (rawLocal) {
      return JSON.parse(rawLocal) as T;
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[run-saas-handoff] pending_local_read_failed', {
      key,
      message: err instanceof Error ? err.message : String(err),
    });
  }

  return undefined;
}

function writeStoredJson(key: string, value: unknown) {
  const serialized = JSON.stringify(value);
  try {
    window.sessionStorage.setItem(key, serialized);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[run-saas-handoff] pending_session_store_failed', {
      key,
      message: err instanceof Error ? err.message : String(err),
    });
  }

  try {
    window.localStorage.setItem(key, serialized);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[run-saas-handoff] pending_local_store_failed', {
      key,
      message: err instanceof Error ? err.message : String(err),
    });
  }
}

function clearStoredJson(key: string) {
  try {
    window.sessionStorage.removeItem(key);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[run-saas-handoff] pending_session_clear_failed', {
      key,
      message: err instanceof Error ? err.message : String(err),
    });
  }

  try {
    window.localStorage.removeItem(key);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[run-saas-handoff] pending_local_clear_failed', {
      key,
      message: err instanceof Error ? err.message : String(err),
    });
  }
}

export type PendingSaasHandoffIntent = {
  source: 'saas';
  entryPath: string;
  sessionId?: string;
  storeUrl?: string;
  ownerAccessToken?: string;
  lastUserMessage?: string;
  lastAssistantMessage?: string;
  createdAt: number;
};

export type PendingSaasHandoffBootstrap = {
  source: 'saas' | 'work_account_bridge';
  entryPath: string;
  sessionId?: string;
  storeUrl?: string;
  returnTo?: string;
  targetHost?: string;
  targetTenantId?: string;
  ownerAccessToken?: string;
  createdAt: number;
};

export type GenericStorageSessionAuthProof = {
  mainDcId: number;
  authKeyHex: string;
  isTest?: true;
};

export type StorageSessionRequestBody =
  | { ownerAccessToken: string; source: 'saas' }
  | {
    authSessionProof: GenericStorageSessionAuthProof;
  };

type BuildStorageSessionRequestOptions = {
  allowSaasBootstrap?: boolean;
  search?: string;
};

export function storePendingSaasHandoffIntent(intent: PendingSaasHandoffIntent) {
  writeStoredJson(PENDING_SAAS_HANDOFF_INTENT_KEY, intent);
}

export function getPendingSaasHandoffIntent() {
  return readStoredJson<PendingSaasHandoffIntent>(PENDING_SAAS_HANDOFF_INTENT_KEY);
}

export function consumePendingSaasHandoffIntent() {
  try {
    const intent = getPendingSaasHandoffIntent();
    if (intent) {
      clearStoredJson(PENDING_SAAS_HANDOFF_INTENT_KEY);
    }
    return intent;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[run-saas-handoff] pending_intent_consume_failed', {
      message: err instanceof Error ? err.message : String(err),
    });
    return undefined;
  }
}

export function clearPendingSaasHandoffIntent() {
  clearStoredJson(PENDING_SAAS_HANDOFF_INTENT_KEY);
}

export function storePendingSaasHandoffBootstrap(hint: PendingSaasHandoffBootstrap) {
  writeStoredJson(PENDING_SAAS_HANDOFF_BOOTSTRAP_KEY, hint);
}

export function getPendingSaasHandoffBootstrap() {
  return readStoredJson<PendingSaasHandoffBootstrap>(PENDING_SAAS_HANDOFF_BOOTSTRAP_KEY);
}

export function getCompatiblePendingSaasHandoffBootstrap(search = window.location.search) {
  const searchParams = new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
  const bootstrapHint = getPendingSaasHandoffBootstrap();
  if (bootstrapHint && bootstrapHint.source !== 'saas' && bootstrapHint.source !== 'work_account_bridge') {
    return undefined;
  }
  const querySource = searchParams.get('run_source');
  const hasExplicitNonSaasSearch = searchParams.toString().length > 0 && !querySource;
  if (hasExplicitNonSaasSearch) {
    return undefined;
  }
  if (querySource && bootstrapHint?.source && querySource !== bootstrapHint.source) {
    return undefined;
  }
  const resolvedSource = querySource || bootstrapHint?.source;
  if (resolvedSource !== 'saas' && resolvedSource !== 'work_account_bridge') {
    return undefined;
  }
  return bootstrapHint;
}

export function clearPendingSaasHandoffBootstrap() {
  clearStoredJson(PENDING_SAAS_HANDOFF_BOOTSTRAP_KEY);
}

function buildAuthSessionProof(): GenericStorageSessionAuthProof | undefined {
  const sessionData = loadStoredSession();
  const mainDcId = sessionData?.mainDcId;
  const authKeyHex = mainDcId !== undefined ? sessionData?.keys?.[mainDcId] : undefined;

  if (
    !mainDcId
    || !authKeyHex
    || !/^[0-9a-f]+$/i.test(authKeyHex)
    || authKeyHex.length % 2 !== 0
  ) {
    return undefined;
  }

  return {
    mainDcId,
    authKeyHex,
    ...(sessionData?.isTest ? { isTest: true } : {}),
  };
}

export function buildStorageSessionRequestBody(
  _currentUserId?: string,
  options?: BuildStorageSessionRequestOptions,
): StorageSessionRequestBody | undefined {
  const search = options?.search ?? window.location.search;
  const ownerAccessToken = options?.allowSaasBootstrap
    ? getCompatiblePendingSaasHandoffBootstrap(search)?.ownerAccessToken?.trim()
    : getPendingSaasHandoffBootstrap()?.ownerAccessToken?.trim();
  if (ownerAccessToken) {
    return {
      ownerAccessToken,
      source: 'saas',
    };
  }

  const authSessionProof = buildAuthSessionProof();
  if (!authSessionProof) {
    return undefined;
  }

  return {
    authSessionProof,
  };
}

export function captureSaasHandoffBootstrapFromSearch(
  search = window.location.search,
  pathname = window.location.pathname,
  hostname = window.location.hostname,
) {
  const searchParams = new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
  const source = searchParams.get('run_source');
  if (source !== 'saas' && source !== 'work_account_bridge') {
    return undefined;
  }
  if (source === 'saas') {
    markPublicSaasWebchatContext(search, pathname, hostname);
  }

  const defaultEntryPath = source === 'work_account_bridge' ? 'work-platform' : 'customer-service';
  const entryPath = searchParams.get('run_entry_path') || defaultEntryPath;
  const storeUrl = searchParams.get('run_store_url') || undefined;
  const sessionId = searchParams.get('run_session_id') || undefined;
  const returnTo = searchParams.get('return_to') || undefined;
  const targetHost = searchParams.get('run_target_host') || undefined;
  const targetTenantId = searchParams.get('run_target_tenant') || undefined;
  const ownerAccessToken = searchParams.get('ownerAccessToken') || undefined;
  const hint = {
    source,
    entryPath,
    ...(sessionId ? { sessionId } : {}),
    ...(storeUrl ? { storeUrl } : {}),
    ...(returnTo ? { returnTo } : {}),
    ...(targetHost ? { targetHost } : {}),
    ...(targetTenantId ? { targetTenantId } : {}),
    ...(ownerAccessToken ? { ownerAccessToken } : {}),
    createdAt: Date.now(),
  };
  storePendingSaasHandoffBootstrap(hint);
  return hint;
}
